import { z } from "zod";

import badwords from "./offensive-words.data";
import type { SlotsSearchResponseV4 } from "@/app/aspenDental2/types/common";

/**
 * Pure helpers for the scheduling widget. Slot clock math is string-parsed
 * from the ISO value on purpose: slot times are office-local, and running
 * them through Date would re-render them in the visitor's timezone - a slot
 * bucketed under one day could then format as a different day or flip AM/PM
 * for a far-away visitor.
 */

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

interface SlotTimeParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/** Office-local display parts straight from the ISO string (no Date parsing). */
export function slotTimeParts(iso: string | null | undefined): SlotTimeParts | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso ?? "");
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
  };
}

/** True when the slot's office-local clock reads before noon. */
export function isMorningSlot(iso: string | null | undefined): boolean {
  const parts = slotTimeParts(iso);
  return parts !== null && parts.hour < 12;
}

/** "9:40 AM" from the office-local ISO value. */
export function formatSlotTime(iso: string | null | undefined): string {
  const parts = slotTimeParts(iso);
  if (!parts) return "";
  const hour = parts.hour % 12 || 12;
  const minute = String(parts.minute).padStart(2, "0");
  return `${hour}:${minute} ${parts.hour >= 12 ? "PM" : "AM"}`;
}

function relativeDayLabel(year: number, month: number, day: number, now: Date): string {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(year, month - 1, day);
  const diffDays = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  return WEEKDAYS[target.getDay()];
}

/** "Today, Aug 26 at 9:40 AM" - the recap line for a chosen slot. */
export function formatSlotLong(iso: string | null | undefined, now: Date = new Date()): string {
  const parts = slotTimeParts(iso);
  if (!parts) return "";
  const dayLabel = relativeDayLabel(parts.year, parts.month, parts.day, now);
  return `${dayLabel}, ${MONTHS[parts.month - 1]} ${parts.day} at ${formatSlotTime(iso)}`;
}

function ordinal(day: number): string {
  const tens = day % 100;
  if (tens >= 11 && tens <= 13) return `${day}th`;
  const suffix = ["th", "st", "nd", "rd"][Math.min(day % 10, 4)] ?? "th";
  return `${day}${suffix}`;
}

interface DayStripDay {
  /** Bucket key, matching groupSlotsByDate's `time.slice(0, 10)` keys. */
  key: string;
  /** "Today" / "Tomorrow" / weekday name. */
  dayLabel: string;
  /** "Aug 26th". */
  dateLabel: string;
}

/** The strip always shows a fixed run of calendar days from today, slots or not. */
export function buildDayStrip(count: number, now: Date = new Date()): DayStripDay[] {
  const days: DayStripDay[] = [];
  for (let offset = 0; offset < count; offset++) {
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
    const key = [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, "0"),
      String(date.getDate()).padStart(2, "0"),
    ].join("-");
    days.push({
      key,
      dayLabel: relativeDayLabel(date.getFullYear(), date.getMonth() + 1, date.getDate(), now),
      dateLabel: `${MONTHS[date.getMonth()]} ${ordinal(date.getDate())}`,
    });
  }
  return days;
}

/** First slot of the earliest day that has one, in strip order. */
export function firstAvailableSlot(
  byDay: Record<string, SlotsSearchResponseV4[]>,
  dayKeys: string[],
): SlotsSearchResponseV4 | null {
  for (const key of dayKeys) {
    const slots = byDay[key];
    if (slots && slots.length > 0) return slots[0];
  }
  return null;
}

/**
 * Ten national digits from whatever the patient typed. A pasted leading
 * country code ("1 555...") is tolerated; anything else non-numeric is
 * stripped before length validation.
 */
export function normalizePhoneDigits(input: string): string {
  let digits = input.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  return digits;
}

/** E.164 form of a validated 10-digit US number. */
export function toE164(tenDigits: string): string {
  return `+1${tenDigits}`;
}

export function formatPhoneDisplay(tenDigits: string): string {
  if (tenDigits.length !== 10) return tenDigits;
  return `(${tenDigits.slice(0, 3)}) ${tenDigits.slice(3, 6)}-${tenDigits.slice(6)}`;
}

/**
 * Email validity, matched to the ClearChoice ruleset (cc-dob-formdef
 * lib/utils/userDetailsValidation.ts `emailSchema` -> Zod's email schema).
 * CC ships `z.email()`; this app pins Zod 3.25, whose equivalent is
 * `z.string().email()` with the identical RFC-shaped regex - a non-empty local
 * part, an "@", a domain, and a dotted TLD. This is stricter than the previous
 * hand-rolled `[^\s@]+@[^\s@]+\.[^\s@]+` (which accepted a single-char TLD like
 * "a@b.c" and, worse, a bare-domain leftover); it rejects Rekha's "@gmail.com"
 * (empty local part) exactly as CC does. The frozen 4-step IdentityForm reuses
 * this same seam, so both schedulers stay consistent.
 */
const EMAIL_SCHEMA = z.string().email();
export function isValidEmail(value: string): boolean {
  return EMAIL_SCHEMA.safeParse(value).success;
}

/**
 * A date-of-birth from the native date input, parsed as LOCAL date parts:
 * `new Date("yyyy-mm-dd")` is UTC and shifts a day in western timezones.
 * Returns null for anything that is not a real, non-future calendar date.
 */
export function parseDateOfBirth(value: string, now: Date = new Date()): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1900 || year > now.getFullYear()) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(year, month - 1, day);
  if (date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  if (date.getTime() > now.getTime()) return null;
  return date;
}

export function ageInYears(dateOfBirth: Date, now: Date = new Date()): number {
  let age = now.getFullYear() - dateOfBirth.getFullYear();
  const beforeBirthday =
    now.getMonth() < dateOfBirth.getMonth() ||
    (now.getMonth() === dateOfBirth.getMonth() && now.getDate() < dateOfBirth.getDate());
  if (beforeBirthday) age -= 1;
  return age;
}

export function formatDateOfBirthDisplay(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return isoDate;
  return `${match[2]}/${match[3]}/${match[1]}`;
}

/** "123 Main St, Chicago, IL 60601" from the bounded office record. */
export function formatOfficeAddress(office: {
  address1?: string;
  city?: string;
  stateCode?: string;
  zipCode?: string;
}): string {
  const cityState = [office.city, office.stateCode].filter(Boolean).join(", ");
  const cityStateZip = [cityState, office.zipCode].filter(Boolean).join(" ");
  return [office.address1, cityStateZip].filter(Boolean).join(", ");
}

/** ISO calendar date for a day `offset` days from now, in local time. */
export function isoDateWithOffset(offset: number, now: Date = new Date()): string {
  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

// ===========================================================================
// Patient-info field validation - ClearChoice ("lolo3000") ruleset parity
// ---------------------------------------------------------------------------
// Ported field-for-field from the ClearChoice scheduler ruleset
// (cc-dob-formdef lib/utils/userDetailsValidation.ts + the ConvertFlow field
// validators). One shared source so BOTH schedulers here - the frozen 4-step
// SchedulingWidget/IdentityForm and the emergency 3-step ThreeStepScheduler -
// enforce identical rules and copy. Each validator returns the exact CC error
// string on failure, or null when the value is acceptable, so callers can drop
// it straight into an inline aria-associated error.
// ===========================================================================

/**
 * The patient-form error copy. English strings are byte-verbatim from CC's
 * `userDetailsErrorMessages` (cc-dob-formdef userDetailsValidation.ts:15-27) so
 * the wording matches the ClearChoice form the client already signed off on.
 * `profanity` is CC's own copy ("Inappropriate text entered", userDetailsValidation.ts:26).
 * The guarantor strings have no CC analog (CC has no minor path); they are Aspen
 * copy for the emergency-visit-for-a-minor flow (a parent/guardian books on the
 * minor's behalf) - see the FULL RULESET note on validateDateOfBirth.
 */
export const PATIENT_FIELD_ERRORS = {
  required: "This field is required",
  firstName: "Please enter a valid first name",
  lastName: "Please enter a valid last name",
  email: "Please enter a valid email address",
  phone: "Please enter a valid phone number",
  minLength: "This field is too short",
  maxLength: "This field is too long",
  dateOfBirth: "Please enter a valid date of birth",
  // TAG-178: a distinct, clear message when the entered/pasted DOB is in the
  // future, instead of the generic "valid date of birth" copy. The native input
  // also carries max=today (dobMaxAttr) so the calendar picker cannot select a
  // future date in the first place; this covers a pasted/typed future value.
  futureDateOfBirth: "Date of birth cannot be in the future",
  // TAG-186: the chosen office's own minimum patient age (Facilities API
  // minimumPatientAge, whole years) is the only DOB floor; {age} renders it.
  // The minor/guarantor path (TAG-171) keeps any under-18 DOB at or past that
  // age bookable - this fires only for a patient the office does not see.
  minimumPatientAge:
    "This office schedules patients age {age} and older. Please call the office for younger patients.",
  // CC's own profanity copy (userDetailsValidation.ts:26 `profanity`).
  profanity: "Inappropriate text entered",
  // Emergency-visit-for-a-minor: NOT a block. When the DOB is under 18 the form
  // reveals a required guarantor (parent/guardian) sub-section so a responsible
  // adult can book on the minor's behalf. This copy prompts for it.
  guarantorRequired: "A parent or guardian must be listed for a patient under 18.",
  relationship: "Please select the relationship to the patient",
} as const;

/**
 * The name character rule. Same SHAPE as CC (userDetailsValidation.ts:36/47) -
 * one-or-more letters, then any number of " ", "-" or "'" separators each
 * followed by one-or-more letters - so it accepts "Mary-Jane", "O'Brien",
 * "D'Angelo", "Anne Marie" and rejects digits, other punctuation, and a
 * leading/trailing/doubled/bare separator. The one deliberate WIDENING over CC:
 * the letter class is Unicode `\p{L}` (with the `u` flag), not ASCII `a-zA-Z`,
 * so legitimate accented patient names - "Jose", "Maria", "Pena", "Renee" and
 * their accented forms - are accepted instead of wrongly rejected. Digits and
 * symbols are still refused (that was Rekha's actual TAG-170 defect), because
 * `\p{L}` matches letters only, never `\p{N}` (numbers) or punctuation.
 */
// eslint-disable-next-line security/detect-unsafe-regex -- same shape as CC userDetailsValidation.ts (same lint suppression there), letter class widened to \p{L}
const NAME_PATTERN = /^\p{L}+([ '-]\p{L}+)*$/u;
const NAME_MIN = 2; // CC .min(2)
const NAME_MAX = 30; // CC .max(30)

// Token separators for the profanity split, matched to CC's offensive-words
// TOKEN_SEPARATORS (whitespace, apostrophe, hyphen) so a multi-token name like
// "john <badword>" is checked token-by-token, not as one opaque string.
const NAME_TOKEN_SEPARATORS = /[\s'-]+/;

/**
 * TAG-172 follow-up (Rekha): the local-part of an email is never split by
 * NAME_TOKEN_SEPARATORS (no @ or . in that set), so a name check applied to
 * an email as-is would silently miss "asshole@example.com" - the whole
 * string is one token that never equals a badword. Split on @ and . too so
 * the local-part and each domain label are checked as their own tokens,
 * matching the shape validateName already gets from a real name field.
 */
const EMAIL_TOKEN_SEPARATORS = /[\s'@.-]+/;

/**
 * True when any separator-split token of `value` is on the ClearChoice
 * offensive-words list. Ported field-for-field from cc-dob-formdef
 * offense-words.ts `unacceptableInputs`: lowercase, split, compare each token.
 */
function containsOffensiveText(value: string, separators: RegExp = NAME_TOKEN_SEPARATORS): boolean {
  return value
    .toLocaleLowerCase()
    .split(separators)
    .some((token) => token.length > 0 && badwords.includes(token));
}

/**
 * Validates a first/last name against CC's ruleset. CC layers the checks in a
 * Zod chain: `.trim().min(2).regex(...).max(30)`, so the FIRST failing rule
 * wins and its message shows. This mirrors that order exactly:
 *   1. required (empty after trim)
 *   2. too short (< 2)
 *   3. invalid characters (fails NAME_PATTERN)  -> firstName/lastName message
 *   4. too long (> 30)
 * `field` selects the CC "valid first name" vs "valid last name" copy.
 * After the shape/length checks pass, the value is run through the CC
 * offensive-words filter (TAG-172): a name containing a listed token returns the
 * CC profanity message ("Inappropriate text entered"). CC applies the profanity
 * filter as a separate submit-time check on the accepted value, so it runs last
 * here too. Returns the error string, or null when valid. Trims like CC's `.trim()`.
 */
export function validateName(raw: string, field: "firstName" | "lastName"): string | null {
  const value = raw.trim();
  if (value.length === 0) return PATIENT_FIELD_ERRORS.required;
  if (value.length < NAME_MIN) return PATIENT_FIELD_ERRORS.minLength;
  if (!NAME_PATTERN.test(value)) return PATIENT_FIELD_ERRORS[field];
  if (value.length > NAME_MAX) return PATIENT_FIELD_ERRORS.maxLength;
  if (containsOffensiveText(value)) return PATIENT_FIELD_ERRORS.profanity;
  return null;
}

/**
 * Validates a US mobile number. CC requires exactly 10 national digits
 * (its Zod phone regex is `/^\(\d{3}\) \d{3}-\d{4}$/` fed by a formatter that
 * strips to 10 digits). We reuse `normalizePhoneDigits` (tolerates a pasted
 * leading "1") and require exactly 10 - so "+1 (630) 238-7897" passes and a
 * 20-digit paste fails. Returns CC's phone message, or null when valid.
 */
export function validatePhone(raw: string): string | null {
  const value = raw.trim();
  if (value.length === 0) return PATIENT_FIELD_ERRORS.required;
  if (normalizePhoneDigits(value).length !== 10) return PATIENT_FIELD_ERRORS.phone;
  return null;
}

/**
 * Format-as-you-type for a US phone, ported from CC's `formatUSTel`
 * (convertFlowFieldValidators.ts:61-70): strip to digits, keep at most 10, and
 * render "(xxx) xxx-xxxx" progressively as the digits arrive. This also HARD
 * CAPS the field at 10 digits - a 20-digit paste can never be typed in, closing
 * Rekha's "20+ digits accepted" hole at the input layer as well as at submit.
 */
export function formatPhoneAsTyped(entry: string): string {
  const digits = entry.replace(/\D+/g, "").slice(0, 10);
  if (!digits) return "";
  const area = digits.slice(0, 3);
  const mid = digits.slice(3, 6);
  const last = digits.slice(6, 10);
  let out = digits.length > 3 ? `(${area}) ${mid}` : area;
  if (digits.length > 6) out += `-${last}`;
  return out;
}

/**
 * A well-formed calendar date from the native date input, WITHOUT the
 * non-future guard - so a caller can tell "not a real date at all" apart from
 * "a real date, but in the future" (TAG-178). Enforces the yyyy-mm-dd shape,
 * a real month/day, and CC's year >= 1900 lower bound, but NOT the year<=now or
 * not-future checks. Returns null only for a malformed / out-of-lower-bound value.
 */
function parseCalendarDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1900) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(year, month - 1, day);
  if (date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
}

/**
 * Validates a date of birth from the native date input. Layered:
 *   1. required (empty)
 *   2. not a real, in-lower-bound calendar date (bad shape, 5-digit year like
 *      "20026", impossible m/d, or a year before 1900) -> generic dateOfBirth
 *   3. a real date but in the FUTURE (TAG-178) -> the distinct future message,
 *      not the generic one, so the patient understands what is wrong
 *   4. a real, non-future date but younger than `minimumPatientAgeYears`
 *      (TAG-186) -> the minimumPatientAge message, which names the age
 * The floor is the chosen office's own value from TAG's Facilities API
 * (`minimumPatientAge`, whole years; staging office 4469 reads 6), read when
 * the office is picked - BB sets no default of its own (Jim Vanaria, TAG,
 * 2026-09-09: Clinical owns any pre-selection default). `null` - no office
 * chosen yet, or the value unavailable - means only a future date is refused.
 * The comparison is by calendar date through ageInYears: the patient must have
 * had their Nth birthday on or before `now`, so a child turning N today passes
 * and one turning N tomorrow does not.
 * A DOB under 18 (at or past the floor) is NOT an error (TAG-171): an
 * emergency dental visit must be bookable for a minor. Age is reported
 * separately via `isMinorDob`, and the form reveals a required guarantor
 * sub-section instead of blocking. Returns the error string, or null when the
 * DATE is valid. `now` is injectable for tests.
 */
export function validateDateOfBirth(
  raw: string,
  now: Date = new Date(),
  minimumPatientAgeYears: number | null = null,
): string | null {
  const value = raw.trim();
  if (value.length === 0) return PATIENT_FIELD_ERRORS.required;
  const calendar = parseCalendarDate(value);
  if (!calendar) return PATIENT_FIELD_ERRORS.dateOfBirth;
  if (calendar.getTime() > now.getTime()) return PATIENT_FIELD_ERRORS.futureDateOfBirth;
  if (minimumPatientAgeYears !== null && ageInYears(calendar, now) < minimumPatientAgeYears) {
    return PATIENT_FIELD_ERRORS.minimumPatientAge;
  }
  return null;
}

/**
 * True when `raw` is a valid DOB that makes the patient under 18 as of `now`.
 * Drives the guarantor reveal (TAG-171). Returns false for an empty, malformed,
 * or out-of-range value (those are surfaced by validateDateOfBirth), and false
 * for an adult DOB. `now` is injectable for tests.
 */
export function isMinorDob(raw: string, now: Date = new Date()): boolean {
  const dob = parseDateOfBirth(raw.trim(), now);
  if (!dob) return false;
  return ageInYears(dob, now) < 18;
}

/**
 * Guarantor relationship options for the minor-booking sub-section (TAG-171).
 * A minimal, sensible set; TAG to confirm the exact field list. "" is the
 * unselected sentinel that validateGuarantorRelationship rejects.
 */
export const GUARANTOR_RELATIONSHIP_OPTIONS = [
  "Parent",
  "Legal guardian",
  "Grandparent",
  "Other",
] as const;
type GuarantorRelationship = (typeof GUARANTOR_RELATIONSHIP_OPTIONS)[number];

/**
 * Validates the guarantor's relationship-to-patient select. Required; must be
 * one of GUARANTOR_RELATIONSHIP_OPTIONS. Returns the error string, or null.
 */
export function validateGuarantorRelationship(raw: string): string | null {
  const value = raw.trim();
  if (value.length === 0) return PATIENT_FIELD_ERRORS.relationship;
  if (!GUARANTOR_RELATIONSHIP_OPTIONS.includes(value as GuarantorRelationship)) {
    return PATIENT_FIELD_ERRORS.relationship;
  }
  return null;
}

/**
 * Validates an email against the CC ruleset (Zod email). Returns CC's email
 * message, or null when valid. Trims first, matching CC's field handling.
 * TAG-172 follow-up (Rekha): the profanity filter originally only ran on
 * name fields, so an offensive local-part ("badword@example.com") passed
 * shape validation untouched. Run the same containsOffensiveText check here,
 * after the shape check, so a malformed address still surfaces the "valid
 * email" message first - matching validateName's check ordering.
 */
export function validateEmail(raw: string): string | null {
  const value = raw.trim();
  if (value.length === 0) return PATIENT_FIELD_ERRORS.required;
  if (!isValidEmail(value)) return PATIENT_FIELD_ERRORS.email;
  if (containsOffensiveText(value, EMAIL_TOKEN_SEPARATORS)) return PATIENT_FIELD_ERRORS.profanity;
  return null;
}

/**
 * The `max` attribute for the native DOB <input type="date">: today's local
 * calendar date. Setting this stops the browser from ever letting a future
 * date - or a 5-digit year, which the native picker also forbids past `max` -
 * be committed, so Rekha's "09/09/20026" cannot be entered at the input layer.
 */
export function dobMaxAttr(now: Date = new Date()): string {
  return isoDateWithOffset(0, now);
}

/**
 * The `min` attribute for the DOB input: Jan 1 1900, matching CC's lower year
 * bound (userDetailsValidation is year >= 1900). Belt-and-suspenders with the
 * `parseDateOfBirth` check so the native picker also refuses an absurd past
 * year.
 */
export const DOB_MIN_ATTR = "1900-01-01";
